import { BeginnerModeLevel } from '@colonist/game-beginner-mode';
import { isValueNumericEnum } from '@colonist/validation';
import { BrowserStorage } from './BrowserStorage.ts';
import { I18n } from '@colonist/i18n';
import { MapSettingEnum, BotDifficulty } from '@colonist/game-base-types';
import { PremadeGameSettings } from '../../../shared/PremadeGameSettings.ts';
import { UIMSceneEnum } from '../../../shared/enums/UIMSceneEnums.ts';
const storage = new BrowserStorage();
(function(StorageWrapper) {
    const lastVersionNo = 'versionno';
    const authToken = 'authtoken';
    const careersBadgeDismissedVersion = 'careersBadgeDismissedVersion';
    const antiAlias = 'antialias';
    const masterVolume = 'mastervolume';
    const isMuted = 'ismuted';
    const resolution = 'resolution';
    const powerPreference = 'powerpreference';
    const animationStatus = 'animationstatus';
    const logRocket = 'logrocket';
    const keyboardShortcuts = 'keyboardshortcuts';
    const lastMembershipIncentivePopupTime = 'lastmembershipincentivepopuptime';
    const lastShareActivityLinkPopupTime = 'lastshareactivitylinkpopuptime';
    const lastPushReferrersToInviteMoreFriendsPopupTime = 'lastpushreferrerstoinvitemorefriendspopuptime';
    const roomSettingsSnapshot = 'roomsettingssnapshot';
    // Legacy per-setting keys shipped in PR #22901. Read only by the one-time
    // migration in getRoomSettingsSnapshotRaw, then deleted.
    // TODO: remove after a release cycle once all clients have migrated.
    const legacyRoomSettingKeys = [
        'roomdicesetting',
        'roomgamespeedsetting',
        'roomfriendlyrobberactivesetting',
        'roommodesetting',
        'roommapsetting',
        'roomprivategamesetting',
        'roomhidebankcardssetting',
        'roomnotrollssetting',
        'roommaxplayerssetting',
        'roomvictorypointstowinsetting',
        'roomcarddiscardlimitsetting'
    ];
    const deviceStatusBarHeight = 'device-statusBarHeight';
    const deviceSafeAreaInsetBottom = 'device-safeAreaInsetBottom';
    const showPortraitGamePlay = 'showportraitgameplay';
    const mLoginBypassed = 'mLoginBypassed';
    const mShowAllowNotificationPopup = 'mShowAllowNotificationPopup';
    const mNotificationUserId = 'mNotificationUserId';
    const mPushNotificationRequestStatus = 'mPushNotificationRequestStatus';
    const mLastRequestPushNotificationTime = 'mLastRequestPushNotificationTime';
    const mRequestPushNotificationTimeData = 'mRequestPushNotificationTimeData';
    const mLastShownAllowNotificationPopup = 'mLastShownAllowNotificationPopup';
    const mUserOrientationControllerJourneyStatus = 'mUserOrientationControllerJourneyStatus';
    const mLocalizedStoreProductAndSubscriptionData = 'mLocalizedStoreProductAndSubscriptionData';
    const mLocalizedStoreProductData = 'mLocalizedStoreProductData';
    const mLastScene = 'mLastScene';
    const shouldShowFeedbackForm = 'shouldshowfeedbackform';
    const lastFeedbackFormDate = 'lastfeedbackformdate';
    const showDiscordLinkingSuccessPopup = 'discordlinkingsuccesspopup';
    const discordOnlineUsernamePopupShown = 'discordonlineusernamepopupshown';
    const discordChangeRegionIncentivePopupShown = 'discordchangeregionincentivepopupshown';
    const discordKeepPlayingOnWebPopupShown = 'discordkeepplayingonwebpopupshown';
    const beginnerModeLevel = 'beginnerModeLevel';
    const giftMetadataId = 'giftMetadataId';
    const showPortraitFeedbackPopup = 'showportraitfeedbackpopup';
    const isLandscapeDeprecated = 'isLandscapeDeprecated';
    const lastTimestampDeprecationPopupShown = 'lastTimestampDeprecationPopupShown';
    const userState = 'userState';
    const language = 'language' // used in service worker, don't change
    ;
    const languagePreferenceSelected = 'languagePreferenceSelected';
    const lastSelectedMatchmakingMode = 'lastselectedmatchmakingmode';
    const lastSelectedBotMap = 'lastselectedbotmap';
    const rushBotGameCompletionCount = 'rushbotgamecompletioncount';
    const playerInfoSectionUsedDevsActive = 'playerinfosectionuseddevsactive';
    const playerInfoSectionCKActive = 'playerinfosectionckactive';
    const playerInfoSectionPiecesActive = 'playerinfosectionpiecesactive';
    const rushGamesPlayed = 'rushgamesplayed';
    const rushBotDifficulty = 'rushbotdifficulty';
    function setLogRocket(active) {
        storage.set(logRocket, active);
    }
    // ====================================================================================================================================
    // LogRocket
    // ====================================================================================================================================
    StorageWrapper.setLogRocket = setLogRocket;
    function getLogRocket() {
        const value = storage.get(logRocket);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getLogRocket = getLogRocket;
    function setAuthToken(token) {
        storage.set(authToken, token);
    }
    // ====================================================================================================================================
    // Auth Token
    // ====================================================================================================================================
    StorageWrapper.setAuthToken = setAuthToken;
    function getAuthToken() {
        return storage.get(authToken);
    }
    StorageWrapper.getAuthToken = getAuthToken;
    function setKeyboardShortcuts(active) {
        storage.set(keyboardShortcuts, active);
    }
    // ====================================================================================================================================
    // Canvas Settings
    // ====================================================================================================================================
    StorageWrapper.setKeyboardShortcuts = setKeyboardShortcuts;
    function getKeyboardShortcuts() {
        const value = storage.get(keyboardShortcuts);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getKeyboardShortcuts = getKeyboardShortcuts;
    function setAntialias(active) {
        storage.set(antiAlias, active);
    }
    StorageWrapper.setAntialias = setAntialias;
    function getAntialias() {
        const value = storage.get(antiAlias);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getAntialias = getAntialias;
    function setResolution(no) {
        storage.set(resolution, no);
    }
    StorageWrapper.setResolution = setResolution;
    function getResolution() {
        const value = storage.get(resolution);
        return value == null || isNaN(Number(value)) ? null : Number(value);
    }
    StorageWrapper.getResolution = getResolution;
    function setPowerPreference(pref) {
        storage.set(powerPreference, pref);
    }
    StorageWrapper.setPowerPreference = setPowerPreference;
    function getPowerPreference() {
        switch(storage.get(powerPreference, 'low-power')){
            case 'low-power':
                return 'low-power';
            case 'high-performance':
                return 'high-performance';
            case 'default':
                return 'default';
            default:
                return 'low-power';
        }
    }
    StorageWrapper.getPowerPreference = getPowerPreference;
    function setAnimation(active) {
        storage.set(animationStatus, active);
    }
    StorageWrapper.setAnimation = setAnimation;
    function getAnimation() {
        const value = storage.get(animationStatus);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getAnimation = getAnimation;
    function setVersionNo(no) {
        storage.set(lastVersionNo, no);
        return true;
    }
    // ====================================================================================================================================
    // Version No
    // ====================================================================================================================================
    StorageWrapper.setVersionNo = setVersionNo;
    function getVersionNo() {
        const versionNo = storage.get(lastVersionNo);
        return versionNo == null ? null : Number(versionNo);
    }
    StorageWrapper.getVersionNo = getVersionNo;
    function setMasterVolume(newVolume) {
        storage.set(masterVolume, newVolume);
    }
    // ====================================================================================================================================
    // Sound
    // ====================================================================================================================================
    StorageWrapper.setMasterVolume = setMasterVolume;
    function getMasterVolume() {
        const volumeVal = storage.get(masterVolume);
        if (volumeVal == null) return undefined;
        let volumeNumber = parseFloat(volumeVal);
        if (isNaN(volumeNumber)) return undefined;
        if (volumeNumber < 0.0) volumeNumber = 0.0;
        if (volumeNumber > 1.0) volumeNumber = 1.0;
        if (!isFinite(volumeNumber)) return undefined;
        return volumeNumber;
    }
    StorageWrapper.getMasterVolume = getMasterVolume;
    function setIsMuted(muted) {
        storage.set(isMuted, muted);
    }
    StorageWrapper.setIsMuted = setIsMuted;
    function getIsMuted() {
        return storage.get(isMuted) === 'true';
    }
    StorageWrapper.getIsMuted = getIsMuted;
    function getEffectiveVolume() {
        if (getIsMuted()) return 0;
        return getMasterVolume();
    }
    StorageWrapper.getEffectiveVolume = getEffectiveVolume;
    function setLastMembershipIncentivePopupTime(membershipIncentivePopupShownTime) {
        storage.set(lastMembershipIncentivePopupTime, membershipIncentivePopupShownTime);
    }
    // ====================================================================================================================================
    // Membership Incentive Popup Timer
    // ====================================================================================================================================
    StorageWrapper.setLastMembershipIncentivePopupTime = setLastMembershipIncentivePopupTime;
    function getLastMembershipIncentivePopupTime() {
        const lastMembershipIncentivePopupTimeString = storage.get(lastMembershipIncentivePopupTime);
        return !lastMembershipIncentivePopupTimeString ? null : new Date(lastMembershipIncentivePopupTimeString);
    }
    StorageWrapper.getLastMembershipIncentivePopupTime = getLastMembershipIncentivePopupTime;
    function setRoomSettingsSnapshot(snapshot) {
        storage.setJSON(roomSettingsSnapshot, snapshot);
    }
    // ====================================================================================================================================
    // Room Game Settings
    // ====================================================================================================================================
    StorageWrapper.setRoomSettingsSnapshot = setRoomSettingsSnapshot;
    function getRoomSettingsSnapshotRaw() {
        const existing = storage.getJSON(roomSettingsSnapshot);
        if (existing != null) return existing;
        const migrated = migrateLegacyRoomSettingKeys();
        if (migrated == null) return null;
        storage.setJSON(roomSettingsSnapshot, migrated);
        for (const key of legacyRoomSettingKeys)storage.delete(key);
        return migrated;
    }
    StorageWrapper.getRoomSettingsSnapshotRaw = getRoomSettingsSnapshotRaw;
    function migrateLegacyRoomSettingKeys() {
        const snapshot = {};
        let hasAny = false;
        const readNumber = (key)=>{
            const value = storage.get(key);
            if (value == null) return undefined;
            const parsed = Number(value);
            if (Number.isNaN(parsed)) return undefined;
            return parsed;
        };
        const readBool = (key)=>{
            const value = storage.get(key);
            if (value == null) return undefined;
            return value == 'true';
        };
        const assign = (field, value)=>{
            if (value == undefined) return;
            snapshot[field] = value;
            hasAny = true;
        };
        assign('modeSetting', readNumber('roommodesetting'));
        assign('mapSetting', readNumber('roommapsetting'));
        assign('maxPlayers', readNumber('roommaxplayerssetting'));
        assign('privateGame', readBool('roomprivategamesetting'));
        assign('hideBankCards', readBool('roomhidebankcardssetting'));
        assign('noTrolls', readBool('roomnotrollssetting'));
        assign('victoryPointsToWin', readNumber('roomvictorypointstowinsetting'));
        assign('cardDiscardLimit', readNumber('roomcarddiscardlimitsetting'));
        assign('diceSetting', readNumber('roomdicesetting'));
        assign('friendlyRobber', readBool('roomfriendlyrobberactivesetting'));
        assign('gameSpeed', readNumber('roomgamespeedsetting'));
        return hasAny ? snapshot : null;
    }
    function clearDeprecationStorage() {
        storage.delete(showPortraitFeedbackPopup);
        storage.delete(isLandscapeDeprecated);
        storage.delete(lastTimestampDeprecationPopupShown);
        storage.delete(mLastRequestPushNotificationTime);
        storage.delete('firstsessiontimestamp');
        storage.delete('portraitmodeenforcementaccepted');
    }
    // ====================================================================================================================================
    // Mobile Device Information
    // ====================================================================================================================================
    StorageWrapper.clearDeprecationStorage = clearDeprecationStorage;
    function shouldResetGameOrientation() {
        const portraitGamePlayEnabled = storage.get(showPortraitGamePlay);
        if (portraitGamePlayEnabled == null) return true;
        return false;
    }
    StorageWrapper.shouldResetGameOrientation = shouldResetGameOrientation;
    function isPortraitGamePlayEnabled() {
        const portraitGamePlayEnabled = storage.get(showPortraitGamePlay);
        return portraitGamePlayEnabled != 'false';
    }
    StorageWrapper.isPortraitGamePlayEnabled = isPortraitGamePlayEnabled;
    function setPortraitGamePlayEnabled(enabled) {
        storage.set(showPortraitGamePlay, enabled);
    }
    StorageWrapper.setPortraitGamePlayEnabled = setPortraitGamePlayEnabled;
    function setStatusBarHeight(value) {
        storage.set(deviceStatusBarHeight, value);
    }
    StorageWrapper.setStatusBarHeight = setStatusBarHeight;
    function setSafeAreaInsetBottom(value) {
        storage.set(deviceSafeAreaInsetBottom, value);
    }
    StorageWrapper.setSafeAreaInsetBottom = setSafeAreaInsetBottom;
    function getIfUserBypassedLogin() {
        const bypassed = storage.get(mLoginBypassed);
        return bypassed == 'true';
    }
    StorageWrapper.getIfUserBypassedLogin = getIfUserBypassedLogin;
    function setUserBypassedLogin(value) {
        storage.set(mLoginBypassed, value);
    }
    StorageWrapper.setUserBypassedLogin = setUserBypassedLogin;
    function setLastShownAllowNotificationPopupTime(date) {
        storage.set(mLastShownAllowNotificationPopup, date instanceof Date ? date.toISOString() : date);
    }
    StorageWrapper.setLastShownAllowNotificationPopupTime = setLastShownAllowNotificationPopupTime;
    function setNotificationUserId(userId) {
        storage.set(mNotificationUserId, userId);
    }
    StorageWrapper.setNotificationUserId = setNotificationUserId;
    function getNotificationUserId() {
        return storage.get(mNotificationUserId);
    }
    StorageWrapper.getNotificationUserId = getNotificationUserId;
    function getLastShownAllowNotificationPopupTime() {
        const timestamp = storage.get(mLastShownAllowNotificationPopup);
        return !timestamp ? null : new Date(timestamp);
    }
    StorageWrapper.getLastShownAllowNotificationPopupTime = getLastShownAllowNotificationPopupTime;
    function setShouldShowAllowNotificationPopup(show) {
        storage.set(mShowAllowNotificationPopup, show);
    }
    StorageWrapper.setShouldShowAllowNotificationPopup = setShouldShowAllowNotificationPopup;
    function getShouldShowAllowNotificationPopup() {
        const show = storage.get(mShowAllowNotificationPopup);
        return show == null ? null : show == 'true';
    }
    StorageWrapper.getShouldShowAllowNotificationPopup = getShouldShowAllowNotificationPopup;
    function setPushNotificationRequestStatus(status) {
        storage.set(mPushNotificationRequestStatus, status);
    }
    StorageWrapper.setPushNotificationRequestStatus = setPushNotificationRequestStatus;
    function getPushNotificationRequestStatus() {
        return storage.get(mPushNotificationRequestStatus) == 'true';
    }
    StorageWrapper.getPushNotificationRequestStatus = getPushNotificationRequestStatus;
    function getLastRequestPushNotificationTime() {
        const timestamp = storage.get(mLastRequestPushNotificationTime);
        return !timestamp ? null : new Date(timestamp);
    }
    StorageWrapper.getLastRequestPushNotificationTime = getLastRequestPushNotificationTime;
    function setRequestPushNotificationTimeData(data) {
        storage.set(mRequestPushNotificationTimeData, JSON.stringify(data));
    }
    StorageWrapper.setRequestPushNotificationTimeData = setRequestPushNotificationTimeData;
    function getRequestPushNotificationTimeData() {
        var _storage_get;
        return JSON.parse((_storage_get = storage.get(mRequestPushNotificationTimeData)) !== null && _storage_get !== void 0 ? _storage_get : '{}');
    }
    StorageWrapper.getRequestPushNotificationTimeData = getRequestPushNotificationTimeData;
    function setUserOrientationControllerJourneyStatus(status) {
        storage.set(mUserOrientationControllerJourneyStatus, status);
    }
    StorageWrapper.setUserOrientationControllerJourneyStatus = setUserOrientationControllerJourneyStatus;
    function getUserOrientationControllerJourneyStatus() {
        return storage.get(mUserOrientationControllerJourneyStatus);
    }
    StorageWrapper.getUserOrientationControllerJourneyStatus = getUserOrientationControllerJourneyStatus;
    function setLocalizedStoreSubscriptionData(data) {
        storage.set(mLocalizedStoreProductAndSubscriptionData, JSON.stringify(data));
    }
    StorageWrapper.setLocalizedStoreSubscriptionData = setLocalizedStoreSubscriptionData;
    function setLocalizedStoreProductData(data) {
        storage.set(mLocalizedStoreProductData, JSON.stringify(data));
    }
    StorageWrapper.setLocalizedStoreProductData = setLocalizedStoreProductData;
    function getLocalizedStoreSubscriptionData() {
        try {
            var _storage_get;
            return JSON.parse((_storage_get = storage.get(mLocalizedStoreProductAndSubscriptionData)) !== null && _storage_get !== void 0 ? _storage_get : '[]');
        } catch  {
            return [];
        }
    }
    StorageWrapper.getLocalizedStoreSubscriptionData = getLocalizedStoreSubscriptionData;
    function getLocalizedStoreProductData() {
        try {
            var _storage_get;
            return JSON.parse((_storage_get = storage.get(mLocalizedStoreProductData)) !== null && _storage_get !== void 0 ? _storage_get : '[]');
        } catch  {
            return [];
        }
    }
    StorageWrapper.getLocalizedStoreProductData = getLocalizedStoreProductData;
    function setLastScene(scene) {
        storage.set(mLastScene, scene);
    }
    StorageWrapper.setLastScene = setLastScene;
    function getLastScene() {
        const scene = storage.get(mLastScene);
        if (scene == null) return null;
        const sceneEnum = Number(scene);
        if (isNaN(sceneEnum)) return null;
        if (!Object.values(UIMSceneEnum).includes(sceneEnum)) return null;
        return sceneEnum;
    }
    StorageWrapper.getLastScene = getLastScene;
    function clearLastScene() {
        storage.delete(mLastScene);
    }
    StorageWrapper.clearLastScene = clearLastScene;
    function removeShouldShowFeedbackForm() {
        storage.delete(shouldShowFeedbackForm);
    }
    // ====================================================================================================================================
    // NPS Form
    // ====================================================================================================================================
    StorageWrapper.removeShouldShowFeedbackForm = removeShouldShowFeedbackForm;
    function setShouldShowFeedbackForm(show) {
        storage.set(shouldShowFeedbackForm, show);
    }
    StorageWrapper.setShouldShowFeedbackForm = setShouldShowFeedbackForm;
    function getShouldShowFeedbackForm() {
        const show = storage.get(shouldShowFeedbackForm);
        return show == null ? null : show == 'true';
    }
    StorageWrapper.getShouldShowFeedbackForm = getShouldShowFeedbackForm;
    function setLastFeedbackFormDate(date) {
        storage.set(lastFeedbackFormDate, date instanceof Date ? date.toISOString() : date);
    }
    StorageWrapper.setLastFeedbackFormDate = setLastFeedbackFormDate;
    function getLastFeedbackFormDate() {
        const lastFeedbackFormDateString = storage.get(lastFeedbackFormDate);
        return !lastFeedbackFormDateString ? null : new Date(lastFeedbackFormDateString);
    }
    StorageWrapper.getLastFeedbackFormDate = getLastFeedbackFormDate;
    function incrementBeginnerModeLevel() {
        const currentLevel = getBeginnerModeLevel();
        if (currentLevel == null) return;
        const nextLevel = currentLevel + 1;
        if (!isValueNumericEnum(nextLevel, BeginnerModeLevel)) return;
        storage.set(beginnerModeLevel, nextLevel);
    }
    // ====================================================================================================================================
    // Beginner Mode
    // ====================================================================================================================================
    StorageWrapper.incrementBeginnerModeLevel = incrementBeginnerModeLevel;
    function decrementBeginnerModeLevel() {
        const currentLevel = getBeginnerModeLevel();
        if (currentLevel == null) return;
        const nextLevel = currentLevel - 1;
        if (!isValueNumericEnum(nextLevel, BeginnerModeLevel)) return;
        storage.set(beginnerModeLevel, nextLevel);
    }
    StorageWrapper.decrementBeginnerModeLevel = decrementBeginnerModeLevel;
    function resetBeginnerModeLevel() {
        storage.set(beginnerModeLevel, BeginnerModeLevel.Level1);
    }
    StorageWrapper.resetBeginnerModeLevel = resetBeginnerModeLevel;
    function getBeginnerModeLevel() {
        const defaultLevel = BeginnerModeLevel.Level1;
        const level = storage.get(beginnerModeLevel);
        if (level == null) {
            storage.set(beginnerModeLevel, defaultLevel);
            return defaultLevel;
        }
        return Number(level);
    }
    StorageWrapper.getBeginnerModeLevel = getBeginnerModeLevel;
    function setDiscordAccountLinked() {
        storage.set(showDiscordLinkingSuccessPopup, true);
    }
    // ====================================================================================================================================
    // Discord Account Linked
    // ====================================================================================================================================
    StorageWrapper.setDiscordAccountLinked = setDiscordAccountLinked;
    function getDiscordAccountLinked() {
        return storage.get(showDiscordLinkingSuccessPopup) == 'true';
    }
    StorageWrapper.getDiscordAccountLinked = getDiscordAccountLinked;
    function removeDiscordAccountLinked() {
        storage.delete(showDiscordLinkingSuccessPopup);
    }
    StorageWrapper.removeDiscordAccountLinked = removeDiscordAccountLinked;
    function setDiscordOnlineUsernamePopupShown() {
        storage.set(discordOnlineUsernamePopupShown, true);
    }
    // ====================================================================================================================================
    // Discord Online Username Popup
    // ====================================================================================================================================
    StorageWrapper.setDiscordOnlineUsernamePopupShown = setDiscordOnlineUsernamePopupShown;
    function getDiscordOnlineUsernamePopupShown() {
        return storage.get(discordOnlineUsernamePopupShown) == 'true';
    }
    StorageWrapper.getDiscordOnlineUsernamePopupShown = getDiscordOnlineUsernamePopupShown;
    function setDiscordChangeRegionIncentivePopupShown() {
        storage.set(discordChangeRegionIncentivePopupShown, true);
    }
    // ====================================================================================================================================
    // Discord Change Region Incentive Popup
    // ====================================================================================================================================
    StorageWrapper.setDiscordChangeRegionIncentivePopupShown = setDiscordChangeRegionIncentivePopupShown;
    function getDiscordChangeRegionIncentivePopupShown() {
        return storage.get(discordChangeRegionIncentivePopupShown) == 'true';
    }
    StorageWrapper.getDiscordChangeRegionIncentivePopupShown = getDiscordChangeRegionIncentivePopupShown;
    function setDiscordKeepPlayingOnWebPopupShown() {
        storage.set(discordKeepPlayingOnWebPopupShown, true);
    }
    // ====================================================================================================================================
    // Discord Keep Playing On Web Popup
    // ====================================================================================================================================
    StorageWrapper.setDiscordKeepPlayingOnWebPopupShown = setDiscordKeepPlayingOnWebPopupShown;
    function getDiscordKeepPlayingOnWebPopupShown() {
        return storage.get(discordKeepPlayingOnWebPopupShown) == 'true';
    }
    StorageWrapper.getDiscordKeepPlayingOnWebPopupShown = getDiscordKeepPlayingOnWebPopupShown;
    function setUserState(state) {
        storage.set(userState, JSON.stringify(state));
    }
    // ====================================================================================================================================
    // UserState
    // ====================================================================================================================================
    StorageWrapper.setUserState = setUserState;
    function clearUserState() {
        setUserState({});
    }
    StorageWrapper.clearUserState = clearUserState;
    function getUserState() {
        const stored = storage.get(userState);
        if (stored == null) return undefined;
        try {
            return JSON.parse(stored);
        } catch  {
            return undefined;
        }
    }
    StorageWrapper.getUserState = getUserState;
    function getLanguage() {
        var _storage_get;
        return (_storage_get = storage.get(language)) !== null && _storage_get !== void 0 ? _storage_get : I18n.defaultLanguage;
    }
    StorageWrapper.getLanguage = getLanguage;
    function setLanguage(value) {
        storage.set(language, value);
        void updateServiceWorkerLanguage(value);
    }
    StorageWrapper.setLanguage = setLanguage;
    function isLanguagePreferenceSelected() {
        return storage.get(languagePreferenceSelected) == 'true';
    }
    StorageWrapper.isLanguagePreferenceSelected = isLanguagePreferenceSelected;
    function setLanguagePreferenceSelected(value) {
        storage.set(languagePreferenceSelected, value);
    }
    StorageWrapper.setLanguagePreferenceSelected = setLanguagePreferenceSelected;
    function setGiftMetadataId(id) {
        storage.set(giftMetadataId, id);
    }
    // ====================================================================================================================================
    // Gifting
    // ====================================================================================================================================
    StorageWrapper.setGiftMetadataId = setGiftMetadataId;
    function getGiftMetadataId() {
        return storage.get(giftMetadataId);
    }
    StorageWrapper.getGiftMetadataId = getGiftMetadataId;
    function clearGiftMetadataId() {
        storage.delete(giftMetadataId);
    }
    StorageWrapper.clearGiftMetadataId = clearGiftMetadataId;
    function setLastShareActivityLinkPopupTime(shareActivityLinkPopupShownTime) {
        storage.set(lastShareActivityLinkPopupTime, shareActivityLinkPopupShownTime);
    }
    // ====================================================================================================================================
    // Share Activity Link Popup Timer
    // ====================================================================================================================================
    StorageWrapper.setLastShareActivityLinkPopupTime = setLastShareActivityLinkPopupTime;
    function getLastShareActivityLinkPopupTime() {
        const lastShareActivityLinkPopupTimeString = storage.get(lastShareActivityLinkPopupTime);
        return !lastShareActivityLinkPopupTimeString ? null : new Date(lastShareActivityLinkPopupTimeString);
    }
    StorageWrapper.getLastShareActivityLinkPopupTime = getLastShareActivityLinkPopupTime;
    function setLastPushReferrersToInviteMoreFriendsPopupTime(pushReferrersToInviteMoreFriendsPopupTime) {
        storage.set(lastPushReferrersToInviteMoreFriendsPopupTime, pushReferrersToInviteMoreFriendsPopupTime);
    }
    // ====================================================================================================================================
    // Push Referrers To Invite More Friends Popup Timer
    // ====================================================================================================================================
    StorageWrapper.setLastPushReferrersToInviteMoreFriendsPopupTime = setLastPushReferrersToInviteMoreFriendsPopupTime;
    function getLastPushReferrersToInviteMoreFriendsPopupTime() {
        const lastPushReferrersToInviteMoreFriendsPopupTimeString = storage.get(lastPushReferrersToInviteMoreFriendsPopupTime);
        return !lastPushReferrersToInviteMoreFriendsPopupTimeString ? null : new Date(lastPushReferrersToInviteMoreFriendsPopupTimeString);
    }
    StorageWrapper.getLastPushReferrersToInviteMoreFriendsPopupTime = getLastPushReferrersToInviteMoreFriendsPopupTime;
    function setLastSelectedMatchmakingMode(matchmakingData) {
        storage.set(lastSelectedMatchmakingMode, JSON.stringify({
            category: matchmakingData.category,
            matchType: matchmakingData.matchType
        }));
    }
    StorageWrapper.setLastSelectedMatchmakingMode = setLastSelectedMatchmakingMode;
    function setPlayerInfoSectionUsedDevsActive(active) {
        storage.set(playerInfoSectionUsedDevsActive, active);
    }
    // ====================================================================================================================================
    // Player Info Section Toggle States
    // ====================================================================================================================================
    StorageWrapper.setPlayerInfoSectionUsedDevsActive = setPlayerInfoSectionUsedDevsActive;
    function getPlayerInfoSectionUsedDevsActive() {
        const value = storage.get(playerInfoSectionUsedDevsActive);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getPlayerInfoSectionUsedDevsActive = getPlayerInfoSectionUsedDevsActive;
    function setPlayerInfoSectionCKActive(active) {
        storage.set(playerInfoSectionCKActive, active);
    }
    StorageWrapper.setPlayerInfoSectionCKActive = setPlayerInfoSectionCKActive;
    function getPlayerInfoSectionCKActive() {
        const value = storage.get(playerInfoSectionCKActive);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getPlayerInfoSectionCKActive = getPlayerInfoSectionCKActive;
    function setPlayerInfoSectionPiecesActive(active) {
        storage.set(playerInfoSectionPiecesActive, active);
    }
    StorageWrapper.setPlayerInfoSectionPiecesActive = setPlayerInfoSectionPiecesActive;
    function getPlayerInfoSectionPiecesActive() {
        const value = storage.get(playerInfoSectionPiecesActive);
        return value == null ? null : value == 'true';
    }
    StorageWrapper.getPlayerInfoSectionPiecesActive = getPlayerInfoSectionPiecesActive;
    function setCareersBadgeDismissed() {
        const version = getVersionNo();
        if (version == null) return;
        storage.set(careersBadgeDismissedVersion, version);
    }
    // ====================================================================================================================================
    // Careers Notification Badge
    // ====================================================================================================================================
    StorageWrapper.setCareersBadgeDismissed = setCareersBadgeDismissed;
    function isCareersBadgeVisible() {
        const dismissedVersion = storage.get(careersBadgeDismissedVersion);
        if (dismissedVersion == null) return true;
        const currentVersion = getVersionNo();
        if (currentVersion == null) return true;
        return Number(dismissedVersion) != currentVersion;
    }
    StorageWrapper.isCareersBadgeVisible = isCareersBadgeVisible;
    function getRushGamesPlayed() {
        const value = storage.get(rushGamesPlayed);
        if (value == null) return 0;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    // ====================================================================================================================================
    // Rush rules dialog (shown until the player completes a Rush game)
    // ====================================================================================================================================
    StorageWrapper.getRushGamesPlayed = getRushGamesPlayed;
    function incrementRushGamesPlayed() {
        storage.set(rushGamesPlayed, getRushGamesPlayed() + 1);
    }
    StorageWrapper.incrementRushGamesPlayed = incrementRushGamesPlayed;
    function getRushBotGameCompletionCount() {
        var _storage_get;
        return Number((_storage_get = storage.get(rushBotGameCompletionCount)) !== null && _storage_get !== void 0 ? _storage_get : 0);
    }
    StorageWrapper.getRushBotGameCompletionCount = getRushBotGameCompletionCount;
    function incrementRushBotGameCompletionCount() {
        storage.set(rushBotGameCompletionCount, getRushBotGameCompletionCount() + 1);
    }
    StorageWrapper.incrementRushBotGameCompletionCount = incrementRushBotGameCompletionCount;
    function setRushBotDifficulty(difficulty) {
        storage.set(rushBotDifficulty, difficulty);
    }
    StorageWrapper.setRushBotDifficulty = setRushBotDifficulty;
    function getRushBotDifficulty() {
        const value = storage.get(rushBotDifficulty);
        if (value == null) return PremadeGameSettings.DEFAULT_RUSH_BOT_DIFFICULTY;
        const difficulty = Number(value);
        if (!isValueNumericEnum(difficulty, BotDifficulty)) return PremadeGameSettings.DEFAULT_RUSH_BOT_DIFFICULTY;
        return difficulty;
    }
    StorageWrapper.getRushBotDifficulty = getRushBotDifficulty;
    function getLastSelectedMatchmakingMode() {
        const stored = storage.get(lastSelectedMatchmakingMode);
        if (stored == null) return null;
        try {
            return JSON.parse(stored);
        } catch  {
            return null;
        }
    }
    StorageWrapper.getLastSelectedMatchmakingMode = getLastSelectedMatchmakingMode;
    function setLastSelectedBotMap(mapSetting) {
        storage.set(lastSelectedBotMap, mapSetting);
    }
    StorageWrapper.setLastSelectedBotMap = setLastSelectedBotMap;
    function getLastSelectedBotMap() {
        const mapSetting = storage.get(lastSelectedBotMap);
        if (mapSetting == null) return null;
        const mapSettingEnum = Number(mapSetting);
        if (isNaN(mapSettingEnum)) return null;
        if (!isValueNumericEnum(mapSettingEnum, MapSettingEnum)) return null;
        return mapSettingEnum;
    }
    StorageWrapper.getLastSelectedBotMap = getLastSelectedBotMap;
})(StorageWrapper || (StorageWrapper = {}));
async function updateServiceWorkerLanguage(language) {
    if ('serviceWorker' in navigator) {
        var _navigator_serviceWorker_controller;
        await navigator.serviceWorker.ready;
        (_navigator_serviceWorker_controller = navigator.serviceWorker.controller) === null || _navigator_serviceWorker_controller === void 0 ? void 0 : _navigator_serviceWorker_controller.postMessage({
            type: 'languageChange',
            language: language
        });
    }
}
export var StorageWrapper;
